/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, HttpService, I18nService, PermissionService } from '@dm/core';
import { type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class CaUploadService {
    i18n = {
        reupload: this.i18nService.get('settings.LDAPsetting_term_reupload_button'),
        cancelReupload: this.i18nService.get('settings.LDAPsetting_term_cancelReupload_button'),
        requiredErr: this.i18nService.get('settings.LDAPsetting_term_uploadCA_tip'),
        uploadCaFile: this.i18nService.get('settings.LDAPsetting_term_saveLDAPServer_uploadCA_lab'),
        checkCaFileSecurity: this.i18nService.get('settings.LDAPsetting_term_saveLDAPServer_checkCASecurity_lab'),
        // eslint-disable-next-line max-len
        uploadFailed: this.i18nService.get('common.upload_fail_label', [this.i18nService.get('settings.certificateManage_task_CACertificateFile_para_label')]),
        common: this.i18nService.get('common.comma_value'),

        activeMsg: this.i18nService.get('settings.certificateManage_task_activateCA_lab'),
        replaceCATitle: this.i18nService.get('settings.LDAPsetting_term_replaceCA_button'),
        replaceMsg: this.i18nService.get('settings.certificateManage_term_replaceSSLCertifacate_label'),
        // eslint-disable-next-line max-len
        replaceWithReasonMsg: (reasons: string): string => this.i18nService.get('settings.certificateManage_task_importCertifacatewithLowerSecure_withReason_msg', [reasons]),
        taskCanceled: this.i18nService.get('common.task_cancle_lab'),
    };
    statusText = {
        0: this.i18nService.get('common.EnumDefine_CERTIFICATE_STATUS_NO_EXIST'),
        1: this.i18nService.get('common.EnumDefine_CERTIFICATE_STATUS_VALID'),
        2: this.i18nService.get('common.EnumDefine_CERTIFICATE_STATUS_INVALID'),
    };

    constructor(
        private i18nService: I18nService,
        private httpService: HttpService,
        private permissionService: PermissionService,
    ) { }

    /**
     * 获取上传证书地址
     * @param certificateType 证书类型
     */
    getUploadFileUrl(certificateType: number, fileUploadExtension?: string): string {
        const rest = this.httpService.makeUrl('certificate.post');
        rest.setParams({
            certificateType,
            fileName: 'CA_CERTIFICATE_NAME',
            fileType: fileUploadExtension || 'crt',
        });
        return rest.url;
    }

    /**
     * 获取证书信息
     * @param certificateType 证书类型
     * @param options 选项
     */
    getInfo(certificateType: number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        if (!this.permissionService.hasPermission('certificate_R')) {
            return of(null);
        }
        const rest = this.httpService.makeUrl('certificate.getBatch');
        return this.httpService.get(rest, options)
            .pipe(
                map(({ data }) => data.find((a) => Number(a.CERTIFICATE_TYPE) === certificateType)),
            );
    }

    /**
     * 校验证书安全性
     * @param certificateType 证书类型
     */
    checkSecurity(certificateType: number): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('certificate.checkSecurity');
        const opt = { CERTIFICATE_TYPE: certificateType };
        return this.httpService.post(rest, opt, { doException: false });
    }

    /**
     * 激活证书
     * @param certificateType 证书类型
     * @param fileName 证书文件名
     */
    active(certificateType: number, fileName: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('certificate.active');
        const opt = {
            CERTIFICATE_TYPE: certificateType,
            CA_CERTIFICATE_NAME: fileName,
        };
        return this.httpService.put(rest, opt, { doException: false });
    }

    /**
     * 获取失败原因文字
     * @param reason 原因
     * 1.证书秘钥长度不安全
     * 2.证书签名算法不安全
     * 3.证书的CN号和阵列的SN号不一致
     * 4.CA证书的秘钥长度不安全
     * 5.CA证书的签名算法不安全
     */
    getReasonOfLowSecurity(reason: number | string): string {
        return this.i18nService.get(`settings.certificateManage_LowerSecure_reason${reason}`);
    }

    getLanguage(): string {
        return this.i18nService.language;
    }
}
